/**
 * **Which shape a "Szybki feedback" card takes, and the facts that shape draws.**
 *
 * One entry point over the queue's payload, so no component ever reads `AttentionItemPayload`. The
 * payload is thirty-four optional nullable fields of which any given card wants six; a card that
 * read it directly would carry the whole of that union in its props and would decide, in JSX, which
 * six are its own.
 *
 * It branches on `reason`, never on a list of four: `ReviewReply` is already specified as a fifth
 * Feedback reason (attention-queue R2) with no enum member yet, so anything this module cannot name
 * falls to `plain` and shows up as a card that works rather than as a blank.
 *
 * ── A freestyle session is told by `sessionKind` and NEVER by `sessionName` ───────────────────
 * An `AdHoc` session has no name, but neither has a planned one whose template was deleted, so
 * `null` is evidence of nothing. The distinction is load-bearing: `completed-session-display` R9
 * forbids an `AdHoc` verdict referencing compliance against a prescription, so the freestyle card
 * draws no ring and no fraction.
 *
 * Pure: no React, no DOM, no query hook, no i18next. What leaves is data and NAMES; the words are
 * the card's.
 */

import type { AttentionReason } from '~/entities/client';
import type {
  TrainWithMeModulesAttentionDtosResponsesAttentionItemPayload as AttentionItemPayload,
  TrainWithMeModulesSurveysPublicSurveyAnswerHighlight as SurveyAnswerHighlight,
} from '~/shared/api/generated/model';

/** The wire spelling of `WorkoutExecutionKind.AdHoc`. The only value that means freestyle. */
const AD_HOC = 'AdHoc';

/** The wire spellings of `SurveyKind`, as the review card tells them apart. */
export type SurveyFlavour = 'intake' | 'checkIn';

/** The wire spellings of a check-in occurrence's outcome (`intake-survey` R28). */
export type CheckInOutcome = 'onTime' | 'late';

/**
 * A finished session waiting for the trainer's word — what the CARD draws, and nothing beside it.
 *
 * The client's own note reaches the row through `model/taskAction`, which is where the composer's
 * destination is decided. It is not lifted twice.
 */
export interface SessionFeedback {
  readonly kind: 'session';
  /**
   * `true` for `AdHoc`. It decides the pill, and it decides whether a ring may be drawn at all.
   */
  readonly freestyle: boolean;
  /** `null` where the wire carried no execution — the card then has nothing to write a note to. */
  readonly workoutExecutionId: number | null;
  /**
   * What the session was called, or `null` where it has no name.
   *
   * The meta line's first half. It is **evidence of nothing** about the session's kind — a planned
   * session whose template was deleted is nameless too, which is why `freestyle` is read off
   * `sessionKind` and never off this (see the header). A freestyle session never has one:
   * `BuildAdHocExecution` does not set it.
   */
  readonly name: string | null;
  /** Counted (non-warm-up) sets logged, and counted sets on the session. Both `null` together. */
  readonly setsDone: number | null;
  readonly setsTotal: number | null;
  /** How long the session actually ran, or `null` where it was never timed. */
  readonly durationSeconds: number | null;
}

/**
 * One answer of a submitted survey, as the wire carries it — `SurveyAnswerHighlight`, added to the
 * queue payload by the backend on 16 September 2026.
 *
 * **Structural, and deliberately so.** The value arrives in the shape its answer type has and the
 * sentence around it is the reader's language's business; the backend's own note says the same. Two
 * exclusions are already made server-side and are NOT repeated here: a health answer never travels
 * (art. 9 RODO, refused by the catalog and by the snapshot's own flag), and a goal, a body
 * measurement, progress photos and a filmed clip have no value in any of these shapes and are
 * passed over rather than sent blank. The list can be empty and usually is on an unanswered draft.
 */
export interface SurveyHighlight {
  /**
   * The standard catalog code, or `null` for a trainer's own question.
   *
   * It is what a client localizes the question by, and it is also what tells a catalog answer's
   * canonical value (`Female`) from a custom question's, which is already the trainer's own words.
   */
  readonly code: string | null;
  /**
   * The label FROZEN on this submission (`intake-survey` R12) — the backend's canonical ENGLISH for
   * a standard question, and the trainer's own text for a custom one.
   */
  readonly label: string;
  /** The wire spelling of `AnswerType`; it says which of the three below carries the answer. */
  readonly answerType: string;
  /** `Number` and `Scale`. A scale is the bare figure: the catalog fixes no bounds for one. */
  readonly number: number | null;
  /** `ShortText`, `LongText`, `SingleChoice`, or a `Date`'s ISO day. */
  readonly text: string | null;
  /** `MultiChoice` — the selections, as the canonical values the catalog offered. */
  readonly choices: readonly string[];
}

/** A submitted survey the trainer has not marked reviewed. The assignment it writes to is
 * `model/taskAction`'s. */
export interface SurveyFeedback {
  readonly kind: 'survey';
  /** `checkIn` unless the wire said `Intake`. `Reassessment` reads as a check-in: it recurs. */
  readonly flavour: SurveyFlavour;
  /** Questions the client actually answered — an empty key is a skip, not an answer. */
  readonly answeredCount: number | null;
  /** `null` for an intake, which has no due date and therefore no outcome (`intake-survey` R28). */
  readonly outcome: CheckInOutcome | null;
  /** What the client actually reported, in the order the trainer composed the template in. */
  readonly highlights: readonly SurveyHighlight[];
}

/** A clip the trainer has not opened. */
export interface ClipFeedback {
  readonly kind: 'clip';
  readonly clipId: number;
  /** The movement's library name, untranslated — the queue carries no content language. */
  readonly exerciseName: string;
  readonly durationSeconds: number | null;
  /** The published film's shape, for the frame's reserve. Both `null` together. */
  readonly width: number | null;
  readonly height: number | null;
  /** The session it was filmed in; `null` on a clip filmed outside one. */
  readonly workoutExecutionId: number | null;
  /**
   * The logger's 1-based index of the anchored set, which counts warm-ups; reps and weight. All
   * three `null` on a clip anchored to no set.
   */
  readonly setIndex: number | null;
  readonly reps: number | null;
  readonly weight: number | null;
  /** The day the movement was performed — not the day the file was uploaded. */
  readonly performedOn: string | null;
}

/**
 * A remark under a clip whose newest entry is the client's.
 *
 * The question itself is NOT here: the item's own sentence (mobile's `clipCommentAwaitingReply.sub`)
 * already interpolates it as `preview`, and the task card reads it from there.
 *
 * The clip's id IS, since 24 September 2026: the task card plays the film at the question's moment
 * (owner: the player beside the question). Replying stays refused (`exercise-clip-timeline` R15).
 */
export interface ClipCommentFeedback {
  readonly kind: 'clipComment';
  /** The clip the remark is under, or `null` on an item that named none — then no player. */
  readonly clipId: number | null;
  /** Where in the film it is pinned, or `null` for an unpinned remark. */
  readonly offsetMs: number | null;
  /** How many entries the thread holds in total; the preview is its newest. */
  readonly discussionCount: number | null;
  readonly performedOn: string | null;
}

/**
 * A Feedback reason this build has no card shape for. Not a failure state: the row still draws the
 * person, the sentence and all three controls.
 */
export interface PlainFeedback {
  readonly kind: 'plain';
}

export type FeedbackDetail =
  | SessionFeedback
  | SurveyFeedback
  | ClipFeedback
  | ClipCommentFeedback
  | PlainFeedback;

const PLAIN: PlainFeedback = { kind: 'plain' };

function numberOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function textOrEmpty(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

function textOrNull(value: string | null | undefined): string | null {
  const trimmed = textOrEmpty(value);
  return trimmed === '' ? null : trimmed;
}

/**
 * One Feedback item's shape and the facts it draws, so that nothing downstream has to know which of
 * the payload's thirty-four fields belong to which reason.
 */
export function feedbackShape(
  reason: AttentionReason,
  payload: AttentionItemPayload | null | undefined,
): FeedbackDetail {
  switch (reason) {
    case 'sessionAwaitingReview':
      return {
        kind: 'session',
        freestyle: payload?.sessionKind === AD_HOC,
        workoutExecutionId: numberOrNull(payload?.workoutExecutionId),
        name: textOrNull(payload?.sessionName),
        setsDone: numberOrNull(payload?.sessionSetsDone),
        setsTotal: numberOrNull(payload?.sessionSetsTotal),
        durationSeconds: numberOrNull(payload?.sessionDurationSeconds),
      };

    case 'surveyAwaitingReview':
      return {
        kind: 'survey',
        // `Reassessment` is a recurring kind like a check-in and shares its card; only `Intake` has
        // no previous occurrence and no due date, which is the whole difference the card draws.
        flavour: payload?.surveyKind === 'Intake' ? 'intake' : 'checkIn',
        answeredCount: numberOrNull(payload?.surveyAnsweredCount),
        outcome: outcomeOf(payload?.surveyCheckInOutcome),
        highlights: highlightsOf(payload?.surveyHighlights),
      };

    case 'newClip': {
      const clipId = numberOrNull(payload?.clipId);
      // No clip id is no poster, no player and no `seen` to post. The row keeps its three controls
      // and loses the frame, rather than reserving a box for a film it cannot name.
      if (clipId === null) return PLAIN;
      return {
        kind: 'clip',
        clipId,
        exerciseName: textOrEmpty(payload?.clipExerciseName),
        durationSeconds: numberOrNull(payload?.clipDurationSeconds),
        width: numberOrNull(payload?.clipWidth),
        height: numberOrNull(payload?.clipHeight),
        workoutExecutionId: numberOrNull(payload?.clipWorkoutExecutionId),
        setIndex: numberOrNull(payload?.clipSetIndex),
        reps: numberOrNull(payload?.clipReps),
        weight: numberOrNull(payload?.clipWeight),
        performedOn: textOrNull(payload?.clipPerformedOn),
      };
    }

    case 'clipCommentAwaitingReply':
      return {
        kind: 'clipComment',
        clipId: numberOrNull(payload?.clipId),
        offsetMs: numberOrNull(payload?.clipCommentOffsetMs),
        discussionCount: numberOrNull(payload?.clipDiscussionCount),
        performedOn: textOrNull(payload?.clipPerformedOn),
      };

    // Every Triage and Win reason, and any Feedback reason this build has no shape for yet.
    default:
      return PLAIN;
  }
}

/**
 * The answers the queue carried, in wire order.
 *
 * An entry with neither a code nor a label is dropped: the card's whole line is "this question, that
 * answer", and a nameless answer is a value with nothing to say what it measures. Everything else
 * travels as it arrived — which of the three value fields is the real one is the answer type's
 * business, and that belongs to whatever draws the answer, not to this shape.
 */
function highlightsOf(
  wire: readonly SurveyAnswerHighlight[] | null | undefined,
): readonly SurveyHighlight[] {
  const highlights: SurveyHighlight[] = [];
  for (const answer of wire ?? []) {
    const code = textOrNull(answer.code);
    const label = textOrEmpty(answer.label);
    if (label === '' && code === null) continue;
    highlights.push({
      code,
      label,
      answerType: textOrEmpty(answer.answerType),
      number: numberOrNull(answer.number),
      text: textOrNull(answer.text),
      choices: answer.choices ?? [],
    });
  }

  return highlights;
}

function outcomeOf(value: string | null | undefined): CheckInOutcome | null {
  if (value === 'OnTime') return 'onTime';
  if (value === 'Late') return 'late';
  return null;
}
